import { useState, useEffect } from "react";
import axios from "axios";
import AdminLayout from "../components/layout/AdminLayout";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { MarketplaceBadge } from "../components/MarketplaceLogo";
import { AdminUrlSubmitAction } from "../components/admin/AdminUrlSubmitModal";
import AdminLinkSubmitForm from "../components/admin/AdminLinkSubmitForm";
import {
  Package,
  Clock,
  CheckCircle2,
  XCircle,
  ExternalLink,
  User,
  Search,
  Globe,
  Loader2,
  Image,
} from "lucide-react";

export default function AdminSubmissionsPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  // Modal states
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [actionType, setActionType] = useState(null); // "approve" | "reject" | "promote"
  const [notes, setNotes] = useState("");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchPendingProducts();
  }, []);

  const fetchPendingProducts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/products/pending`, {
        withCredentials: true,
      });
      setProducts(response.data.products || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error("Failed to load pending submissions");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedProduct) return;

    setProcessing(true);
    try {
      await axios.post(
        `${API}/admin/products/${selectedProduct.product_id}/approve`,
        { notes: notes || null },
        { withCredentials: true }
      );
      toast.success("Product approved!");
      closeModal();
      fetchPendingProducts();
    } catch (error) {
      console.error("Error approving:", error);
      toast.error(error.response?.data?.detail || "Failed to approve");
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedProduct) return;
    if (!notes.trim()) {
      toast.error("Please provide a reason for rejection");
      return;
    }

    setProcessing(true);
    try {
      await axios.post(
        `${API}/admin/products/${selectedProduct.product_id}/reject`,
        { notes },
        { withCredentials: true }
      );
      toast.success("Product rejected");
      closeModal();
      fetchPendingProducts();
    } catch (error) {
      console.error("Error rejecting:", error);
      toast.error(error.response?.data?.detail || "Failed to reject");
    } finally {
      setProcessing(false);
    }
  };

  const handlePromoteGlobal = async (product) => {
    setProcessing(true);
    try {
      await axios.post(
        `${API}/admin/products/${product.product_id}/promote-global`,
        {},
        { withCredentials: true }
      );
      toast.success("Product promoted to global catalog!");
      fetchPendingProducts();
    } catch (error) {
      console.error("Error promoting:", error);
      toast.error(error.response?.data?.detail || "Failed to promote");
    } finally {
      setProcessing(false);
    }
  };

  const openActionModal = (product, type) => {
    setSelectedProduct(product);
    setActionType(type);
    setNotes("");
  };

  const closeModal = () => {
    setSelectedProduct(null);
    setActionType(null);
    setNotes("");
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const filteredProducts = products.filter((product) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      product.title?.toLowerCase().includes(searchLower) ||
      product.product_id?.toLowerCase().includes(searchLower) ||
      product.source_platform?.toLowerCase().includes(searchLower) ||
      product.submitter?.email?.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <AdminLayout>
        <div className="space-y-4">
          <Skeleton className="h-8 w-64" />
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-32 w-full" />
            ))}
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="submissions-title">
              <Clock className="inline h-6 w-6 mr-2 text-orange-600" />
              Product Submissions
            </h1>
            <p className="text-slate-600 mt-1">
              {total} submission{total !== 1 ? "s" : ""} pending review
            </p>
          </div>
          <div className="flex items-center gap-2">
            <AdminUrlSubmitAction onSubmitted={fetchPendingProducts} />
            <Button variant="outline" onClick={fetchPendingProducts}>
              Refresh
            </Button>
          </div>
        </div>

        {/* Inline import form */}
        <AdminLinkSubmitForm compact onSubmitted={fetchPendingProducts} />

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by title, platform, submitter..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-submissions"
          />
        </div>

        {/* Products List */}
        {filteredProducts.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border border-slate-200">
            <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900">No Pending Submissions</h3>
            <p className="text-slate-500 mt-1">All product submissions have been reviewed</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredProducts.map((product) => (
              <div
                key={product.product_id}
                className="bg-white rounded-lg border border-slate-200 p-4 hover:shadow-md transition-shadow"
                data-testid={`submission-${product.product_id}`}
              >
                <div className="flex gap-4">
                  {/* Image */}
                  <div className="w-24 h-24 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0">
                    {product.images?.[0] ? (
                      <img
                        src={product.images[0]}
                        alt={product.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Image className="h-8 w-8 text-slate-300" />
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-semibold text-slate-900 line-clamp-1">
                          {product.title || product.name}
                        </h3>
                        <div className="flex items-center gap-2 mt-1">
                          <MarketplaceBadge
                            marketplace={product.source_platform || product.marketplace}
                            size="xs"
                          />
                          <Badge variant="outline" className="text-xs">
                            {product.category || "General"}
                          </Badge>
                        </div>
                      </div>
                      <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100 flex-shrink-0">
                        <Clock className="h-3 w-3 mr-1" />
                        Pending
                      </Badge>
                    </div>

                    <div className="grid grid-cols-2 gap-x-6 gap-y-1 mt-3 text-sm">
                      <div className="flex items-center gap-1 text-slate-500">
                        <User className="h-3 w-3" />
                        <span>{product.submitter?.email || "Unknown"}</span>
                      </div>
                      <div className="text-slate-500">
                        Submitted: {formatDate(product.created_at)}
                      </div>
                      {product.source_url && (
                        <a
                          href={product.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline flex items-center gap-1 col-span-2"
                        >
                          <ExternalLink className="h-3 w-3" />
                          View Original URL
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2 flex-shrink-0">
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => openActionModal(product, "approve")}
                      data-testid={`approve-${product.product_id}`}
                    >
                      <CheckCircle2 className="h-4 w-4 mr-1" />
                      Approve
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 border-red-300 hover:bg-red-50"
                      onClick={() => openActionModal(product, "reject")}
                      data-testid={`reject-${product.product_id}`}
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      Reject
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Action Modal */}
        <Dialog open={!!selectedProduct && !!actionType} onOpenChange={closeModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {actionType === "approve" ? (
                  <span className="flex items-center gap-2 text-green-600">
                    <CheckCircle2 className="h-5 w-5" />
                    Approve Product
                  </span>
                ) : (
                  <span className="flex items-center gap-2 text-red-600">
                    <XCircle className="h-5 w-5" />
                    Reject Product
                  </span>
                )}
              </DialogTitle>
            </DialogHeader>

            {selectedProduct && (
              <div className="space-y-4">
                <div className="flex gap-3">
                  {selectedProduct.images?.[0] && (
                    <img
                      src={selectedProduct.images[0]}
                      alt=""
                      className="w-16 h-16 object-cover rounded"
                    />
                  )}
                  <div>
                    <p className="font-semibold">{selectedProduct.title}</p>
                    <p className="text-sm text-slate-500">
                      {selectedProduct.source_platform} • {selectedProduct.category}
                    </p>
                    <p className="text-sm text-slate-500">
                      By: {selectedProduct.submitter?.email}
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {actionType === "approve" ? "Notes (optional)" : "Rejection Reason (required)"}
                  </label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder={
                      actionType === "approve"
                        ? "Add any notes..."
                        : "Explain why this product is being rejected..."
                    }
                    rows={3}
                    data-testid="action-notes"
                  />
                </div>

                {actionType === "approve" && (
                  <div className="bg-green-50 rounded-lg p-3 text-sm text-green-700">
                    <strong>Approval:</strong> The product will become visible in the user&apos;s 
                    catalog. You can later promote it to the global catalog.
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={closeModal} disabled={processing}>
                Cancel
              </Button>
              {actionType === "approve" ? (
                <Button
                  className="bg-green-600 hover:bg-green-700"
                  onClick={handleApprove}
                  disabled={processing}
                  data-testid="confirm-approve"
                >
                  {processing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Approving...
                    </>
                  ) : (
                    "Approve Product"
                  )}
                </Button>
              ) : (
                <Button
                  className="bg-red-600 hover:bg-red-700"
                  onClick={handleReject}
                  disabled={processing}
                  data-testid="confirm-reject"
                >
                  {processing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Rejecting...
                    </>
                  ) : (
                    "Reject Product"
                  )}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
